/**
 * Merchant List Page — Searchable list of Merchant Groups in the Vault
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../lib/auth-store';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Store, Plus, Search, Building2, MapPin, CreditCard, ChevronRight,
  Users, FileText
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

import BookTransferDialog from '../components/merchant/BookTransferDialog';
import { ArrowRightLeft } from 'lucide-react';

export default function MerchantsPage() {
  const { token, user } = useAuthStore();
  const navigate = useNavigate();
  const [showTransfer, setShowTransfer] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ group_name: '', primary_contact: '', primary_phone: '', primary_email: '', industry: '' });
  const [creating, setCreating] = useState(false);

  const fetchGroups = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const res = await fetch(`${API}/api/merchants/groups?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setGroups(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token, search]);

  useEffect(() => {
    const timer = setTimeout(fetchGroups, 300);
    return () => clearTimeout(timer);
  }, [fetchGroups]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await fetch(`${API}/api/merchants/groups`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        const group = await res.json();
        setShowCreate(false);
        setForm({ group_name: '', primary_contact: '', primary_phone: '', primary_email: '', industry: '' });
        navigate(`/merchants/${group.id}`);
      }
    } catch (e) { console.error(e); }
    finally { setCreating(false); }
  };

  return (
    <div className="space-y-6" data-testid="merchants-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Store className="w-6 h-6" />
            Merchants
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {groups.length} merchant group{groups.length !== 1 ? 's' : ''} in vault
          </p>
        </div>
        <div className="flex items-center gap-2">
          {['super_admin', 'iso'].includes(user?.role) && (
            <Button variant="outline" data-testid="open-book-transfer-btn" onClick={() => setShowTransfer(true)} className="gap-2">
              <ArrowRightLeft className="w-4 h-4" /> <span className="hidden sm:inline">Book transfer</span>
            </Button>
          )}
          <Button
            data-testid="create-merchant-btn"
            onClick={() => setShowCreate(!showCreate)}
            className="gap-2"
          >
            <Plus className="w-4 h-4" /> New Merchant
          </Button>
        </div>
      </div>
      {showTransfer && <BookTransferDialog open onClose={() => setShowTransfer(false)} onDone={fetchGroups} />}

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          data-testid="merchant-search"
          placeholder="Search by name, email, phone..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Create Modal */}
      {showCreate && (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Create Merchant Group</h3>
          <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Group / Brand Name *</label>
              <Input
                data-testid="merchant-name-input"
                required
                value={form.group_name}
                onChange={(e) => setForm(f => ({ ...f, group_name: e.target.value }))}
                placeholder="e.g. La Casita Restaurants"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Primary Contact</label>
              <Input
                value={form.primary_contact}
                onChange={(e) => setForm(f => ({ ...f, primary_contact: e.target.value }))}
                placeholder="Contact name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Phone</label>
              <Input
                value={form.primary_phone}
                onChange={(e) => setForm(f => ({ ...f, primary_phone: e.target.value }))}
                placeholder="305-555-1234"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Email</label>
              <Input
                value={form.primary_email}
                onChange={(e) => setForm(f => ({ ...f, primary_email: e.target.value }))}
                placeholder="contact@business.com"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Industry</label>
              <Input
                value={form.industry}
                onChange={(e) => setForm(f => ({ ...f, industry: e.target.value }))}
                placeholder="Restaurant, Retail, etc."
              />
            </div>
            <div className="flex items-end gap-2">
              <Button type="submit" disabled={creating} data-testid="save-merchant-btn">
                {creating ? 'Creating...' : 'Create Merchant'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </form>
        </div>
      )}

      {/* Merchant List */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading merchants...</div>
      ) : groups.length === 0 ? (
        <div className="text-center py-12">
          <Store className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">No merchants yet</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Create a merchant group or convert a lead</p>
        </div>
      ) : (
        <div className="grid gap-3">
          {groups.map((g) => (
            <div
              key={g.id}
              data-testid={`merchant-row-${g.id}`}
              onClick={() => navigate(`/merchants/${g.id}`)}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-blue-300 dark:hover:border-blue-600 cursor-pointer transition-all group"
            >
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                    <Store className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                      {g.group_name}
                    </h3>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400 mt-1 break-all">
                      {g.primary_contact && <span>{g.primary_contact}</span>}
                      {g.primary_email && <span>{g.primary_email}</span>}
                      {g.industry && (
                        <span className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">{g.industry}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-4 sm:gap-6">
                  <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1" title="Legal Entities">
                      <Building2 className="w-3.5 h-3.5" /> {g.entity_count || 0}
                    </span>
                    <span className="flex items-center gap-1" title="Locations">
                      <MapPin className="w-3.5 h-3.5" /> {g.location_count || 0}
                    </span>
                    <span className="flex items-center gap-1" title="Processing Accounts">
                      <CreditCard className="w-3.5 h-3.5" /> {g.processing_account_count || 0}
                    </span>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    g.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                    'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                  }`}>
                    {g.status || 'active'}
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
